import {useCallback, useContext, useEffect, useState, useRef} from 'react'
import {useParams, useSearchParams} from 'react-router-dom'
import {Spin, Modal} from 'antd'
import Spinner from 'components/spinner/Spinner'
import {
  ExternalLink,
  FileText,
  Link2,
  Plus,
  FilesIcon,
  ArrowLeft,
} from 'lucide-react'
import {useSelector} from 'react-redux'
import TabSectionCard from '../../CustomerPatientProfile/components/TabSectionCard'
import EmptyState from '../../CustomerPatientProfile/components/EmptyState'
import CollapseCardWithBorder from '../../CustomerPatientProfile/components/CollapseCardWithBorder'
import {FileSections, getIcon} from '../../CustomerPatientProfile/tabs/Records'
import {safeParseInt} from 'utils/ConstFunctions'
import dayjs from 'dayjs'
import userTypes from '@constants/userTypes'
import {downloadBlob} from 'utils/download'
import JSZip from 'jszip'
import {downloadFile} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfileFiles.slice'
import {getVspCaseRecordsByPatient} from 'redux/Slices/AppSlice/VSP/caserecord.slice'
import {AuthContext} from 'context/AuthContext'
import useDispatchAction from '@hooks/useDispatchAction'
import {VspCaseRecordsForm} from 'screens/VSP/CreateOrder/components/VspCaseRecordForm'
import {RootState} from 'redux/store'
import AntdMessage from 'components/modal/Alert/AntdMessage'

declare const window: Window &
  typeof globalThis & {
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
  }

type CaseRecordFile = {
  file_id: number
  drive_file_id?: string
  name: string
  url: string
  thumbnail_url?: string
  download_url?: string
  full_path?: string
  extension?: string
  size?: number
  created_at?: string
  is_gdrive_platform?: boolean
}

type CaseRecord = {
  id: number
  order_id: string
  extraoral_photo_files?: CaseRecordFile[]
  intraoral_photo_files?: CaseRecordFile[]
  intraoral_scan_files?: CaseRecordFile[]
  stone_cast_files?: CaseRecordFile[]
  dicom_files?: CaseRecordFile[]
  radio_grap_files?: CaseRecordFile[]
  external_links?: string[]
  created_at?: string
}

// 500 MB in bytes
const SIZE_LIMIT_BYTES = 200 * 1024 * 1024

/**
 * Converts Google Drive sharing/view URLs to direct download URLs,
 * bypassing the virus-scan interstitial page for large files.
 */
export const convertToDirectDownloadUrl = (url: string): string => {
  try {
    if (
      url.includes('drive.usercontent.google.com') ||
      url.includes('export=download')
    ) {
      return url
    }
    const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/)
    if (fileMatch) {
      return `https://drive.usercontent.google.com/download?id=${fileMatch[1]}&export=download&authuser=0`
    }
    const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/)
    if (idMatch) {
      return `https://drive.usercontent.google.com/download?id=${idMatch[1]}&export=download&authuser=0`
    }
  } catch {
    // fall through
  }
  return url
}

// ─── Drive Webview Overlay ────────────────────────────────────────────────────
// Renders as a full-screen overlay on top of the current page (fixed inset-0).
// The records page remains mounted in the background — state is fully preserved.
// Clicking Back simply hides the overlay.

type DriveWebviewProps = {
  files: CaseRecordFile[]
  onBack: () => void
}

export const DriveWebview = ({files, onBack}: DriveWebviewProps) => {
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null)
  const [selectedFileName, setSelectedFileName] = useState<string>('')
  const [selectedFileType, setSelectedFileType] = useState<'image' | 'pdf' | 'zip' | 'other' | null>(null)

  const getFileType = (fileName: string): 'image' | 'pdf' | 'zip' | 'other' => {
    const extension = fileName.split('.').pop()?.toLowerCase() || ''
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(extension)) return 'image'
    if (extension === 'pdf') return 'pdf'
    if (extension === 'zip') return 'zip'
    return 'other'
  }

  const handleFileClick = (file: CaseRecordFile) => {
    // Prevent duplicate selection
    if (selectedFileName === file.name) return
    
    const url = convertToDirectDownloadUrl(file?.download_url || file?.url || '')
    const fileType = getFileType(file.name)
    
    setSelectedFileUrl(url)
    setSelectedFileName(file.name)
    setSelectedFileType(fileType)
  }

  return (
    <div className='fixed inset-0 z-50 flex flex-col bg-white'>
      {/* Header */}
      <div className='flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white shrink-0'>
        <button
          onClick={onBack}
          className='flex items-center gap-1.5 text-sm font-medium text-primaryColor hover:opacity-75 transition-opacity'
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-semibold text-gray-800'>
            Clinical Records ({files.length} files)
          </p>
        </div>
      </div>

      {/* Main content with two columns: list and preview */}
      <div className='flex-1 w-full overflow-hidden flex'>
        {/* Files List - Left side */}
        <div className='w-full md:w-1/3 overflow-y-auto border-r border-gray-200 p-4'>
          <div className='grid gap-2'>
            {files.map((file, index) => (
                <div
                  key={file.file_id}
                  onClick={() => handleFileClick(file)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedFileName === file.name
                      ? 'border-primaryColor bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className='flex items-start gap-3 min-w-0'>
                    <div className='text-lg font-semibold text-gray-400 w-8 text-center shrink-0'>
                      {index + 1}
                    </div>
                    <div className='min-w-0 flex-1'>
                      <p className='text-sm font-medium text-gray-800 truncate'>
                        {file.name}
                      </p>
                      {file.size && (
                        <p className='text-xs text-gray-500'>
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      )}
                    </div>
                  </div>
                </div>
            ))}
          </div>
        </div>

        {/* Preview - Right side */}
        <div className='hidden md:flex flex-1 flex-col overflow-hidden bg-gray-50'>
          {selectedFileUrl ? (
            <>
              <div className='p-3 border-b border-gray-200 bg-white shrink-0 flex justify-between items-center'>
                <p className='text-sm font-medium text-gray-700 truncate'>
                  {selectedFileName}
                </p>
                <a
                  href={selectedFileUrl}
                  download={selectedFileName}
                  className='text-xs font-medium text-primaryColor hover:opacity-75 transition-opacity'
                >
                  Download
                </a>
              </div>
              
              {/* Images/PDFs/ZIPs: do not preview inline - show placeholder and require explicit download */}
              {(selectedFileType === 'image' || selectedFileType === 'pdf' || selectedFileType === 'zip') && (
                <div className='flex-1 flex items-center justify-center p-6'>
                  <div className='text-center'>
                    <p className='text-sm text-gray-600 mb-2'>Preview not available for images, PDFs, or ZIPs.</p>
                    <p className='text-xs text-gray-400'>Click "Download" to save the file to your device.</p>
                  </div>
                </div>
              )}
              
              {/* Other files */}
              {selectedFileType === 'other' && (
                <iframe
                  src={selectedFileUrl}
                  className='flex-1 w-full border-none'
                  title={selectedFileName}
                  sandbox='allow-scripts allow-same-origin allow-forms allow-downloads allow-popups'
                />
              )}
            </>
          ) : (
            <div className='flex-1 flex items-center justify-center'>
              <p className='text-gray-400'>Click on a file to preview</p>
            </div>
          )}
        </div>

        {/* Mobile: Full-width preview */}
        {selectedFileUrl && (
          <div className='flex md:hidden absolute inset-0 z-10 flex-col bg-white'>
            <div className='flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white shrink-0'>
              <button
                onClick={() => {
                  setSelectedFileUrl(null)
                  setSelectedFileName('')
                  setSelectedFileType(null)
                }}
                className='flex items-center gap-1.5 text-sm font-medium text-primaryColor hover:opacity-75 transition-opacity'
              >
                <ArrowLeft size={18} />
                <span>Back to List</span>
              </button>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-semibold text-gray-800 truncate'>{selectedFileName}</p>
              </div>
              <a
                href={selectedFileUrl}
                download={selectedFileName}
                className='text-xs font-medium text-primaryColor hover:opacity-75 transition-opacity shrink-0'
              >
                Download
              </a>
            </div>
            
            {/* Images/PDFs/ZIPs: do not preview inline on mobile either */}
            {(selectedFileType === 'image' || selectedFileType === 'pdf' || selectedFileType === 'zip') && (
              <div className='flex-1 flex items-center justify-center p-6'>
                <div className='text-center'>
                  <p className='text-sm text-gray-600 mb-2'>Preview not available for images, PDFs, or ZIPs.</p>
                  <p className='text-xs text-gray-400'>Tap "Download" to save the file to your device.</p>
                </div>
              </div>
            )}
            
            {/* Other files */}
            {selectedFileType === 'other' && (
              <iframe
                src={selectedFileUrl}
                className='flex-1 w-full border-none'
                title={selectedFileName}
                sandbox='allow-scripts allow-same-origin allow-forms allow-downloads allow-popups'
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

const VspRecords = () => {
  const {userId} = useContext(AuthContext)
  const {serviceConfig} = useSelector((state: RootState) => state.serviceConfiguration)
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('order_id')
  const [caseRecords, setCaseRecords] = useState<CaseRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [sectionDownloading, setSectionDownloading] = useState(false)
  const {dispatchAction} = useDispatchAction()
  const {patientId} = useParams()

  // DriveWebview overlay state — open=true shows the overlay, records page stays mounted
  const [driveWebview, setDriveWebview] = useState<{open: boolean; files: CaseRecordFile[]}>({
    open: false,
    files: [],
  })

  const [isCaseRecordFormOpen, setIsCaseRecordFormOpen] = useState(false)
  const [isSubmittingForm, setIsSubmittingForm] = useState(false)
  const [hasUploaderInProgress, setHasUploaderInProgress] = useState(false)
  const [showExternalLinksConfirmModal, setShowExternalLinksConfirmModal] = useState(false)
  const [externalLinksInForm, setExternalLinksInForm] = useState<string[]>([])
  const submitRef = useRef<(() => Promise<boolean>) | null>(null)
  const {uploadingFiles} = useSelector((state: RootState) => state.leadsProfileFiles)
  const isUploading = uploadingFiles || hasUploaderInProgress
  const isSubmitting = isUploading || isSubmittingForm

  const handleCaseRecordSubmit = async () => {
    if (!submitRef.current) return
    setIsSubmittingForm(true)
    const success = await submitRef.current()
    if (!success) {
      setIsSubmittingForm(false)
    }
  }

  const fetchCaseRecords = useCallback(async () => {
    if (!patientId) return
    setLoading(true)
    try {
      const response = await dispatchAction(
        getVspCaseRecordsByPatient({patient_id: patientId})
      ).unwrap()
      setCaseRecords(Array.isArray(response) ? (response as any[]) : [])
    } catch (error) {
      console.error('Failed to fetch VSP case records', error)
    } finally {
      setLoading(false)
    }
  }, [patientId, dispatchAction])

  useEffect(() => {
    fetchCaseRecords()
  }, [fetchCaseRecords])

  const allPhotos = caseRecords.flatMap((r) => [
    ...(r.extraoral_photo_files ?? []),
    ...(r.intraoral_photo_files ?? []),
  ])
  const allScans = caseRecords.flatMap((r) => [
    ...(r.intraoral_scan_files ?? []),
    ...(r.dicom_files ?? []),
  ])
  const allDicom = caseRecords.flatMap((r) => [...(r.dicom_files ?? [])])
  const hasData = allPhotos.length > 0 || allScans.length > 0 || allDicom.length > 0

  const getZipFileName = () => {
    const suffix = orderId || patientId || 'records'
    return `Clinical_Records_${suffix}.zip`
  }

  /**
   * Original zip-via-API download. Only called when total size <= 500 MB.
   */
  const handleDownloadViaApi = async (allFiles: CaseRecordFile[]) => {
    if (!userId) {
      AntdMessage({type: 'error', text: 'Unable to download files. Missing user context.'})
      return
    }

    const zipFileName = getZipFileName()

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          zip_name: zipFileName,
          file_details: allFiles.map((file) => ({
            file_id: file.file_id,
            name: file.name,
            url: file.download_url || file.url,
            download_url: file.download_url || file.url,
            type: (file as any).type || 'application/octet-stream',
            extension: file.extension,
          })),
        })
      )
      return
    }

    setSectionDownloading(true)
    try {
      const zip = new JSZip()
      let count = 0
      for (const file of allFiles) {
        try {
          const response = await dispatchAction(
            downloadFile({
              requester_user_id: safeParseInt(userId),
              requester_user_type: userTypes.DOCTOR,
              file_id: safeParseInt(file.file_id),
            })
          ).unwrap()
          const blob = new Blob([response], {
            type: (file as any)?.type || 'application/octet-stream',
          })
          zip.file(file?.name || `file-${file?.file_id}.dat`, blob)
          count++
        } catch (err) {
          console.error(`Error downloading file_id ${file?.file_id}`, err)
        }
      }
      if (count > 0) {
        const zipBlob = await zip.generateAsync({type: 'blob'})
        downloadBlob(zipBlob, zipFileName)
        return
      }
      AntdMessage({type: 'error', text: 'Unable to download files'})
    } catch (error) {
      console.error('Error downloading VSP files', error)
      AntdMessage({type: 'error', text: 'Error downloading files'})
    } finally {
      setSectionDownloading(false)
    }
  }

  const handleDownloadAll = async () => {
    const uniqueFiles = new Map<number, CaseRecordFile>()
    caseRecords.forEach((record) => {
      const allRecordFiles: CaseRecordFile[] = [
        ...(record.extraoral_photo_files ?? []),
        ...(record.intraoral_photo_files ?? []),
        ...(record.intraoral_scan_files ?? []),
        ...(record.stone_cast_files ?? []),
        ...(record.dicom_files ?? []),
        ...(record.radio_grap_files ?? []),
      ]
      allRecordFiles.forEach((file) => {
        if (file?.file_id) uniqueFiles.set(file.file_id, file)
      })
    })

    const allFiles = Array.from(uniqueFiles.values())
    if (!allFiles.length) {
      AntdMessage({type: 'info', text: 'No files available to download'})
      return
    }

    const totalSize = allFiles.reduce((sum, file) => sum + (file.size ?? 0), 0)

    if (totalSize > SIZE_LIMIT_BYTES) {
      // Open Drive webview overlay — records page stays mounted behind it
      setDriveWebview({open: true, files: allFiles})
    } else {
      await handleDownloadViaApi(allFiles)
    }
  }

  const getText = sectionDownloading ? 'Downloading...' : 'Download All (.zip)'

  const extraActionButtons = (
    <button
      onClick={() => setIsCaseRecordFormOpen(true)}
      className='flex items-center gap-1 bg-primaryColor text-white border-primaryColor px-2 py-1.5 rounded-lg border text-sm font-semibold transition-colors uppercase tracking-wide'
      style={{whiteSpace: 'nowrap'}}
    >
      <Plus size={20} />
      <span>Add Case Record</span>
    </button>
  )

  const caseRecordsToDisplay = [...caseRecords]
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
      return dateB - dateA
    })
    .map((record, index, arr) => ({
      ...record,
      recordNumber: arr.length - index,
    }))

  return (
    <>
      {/* Drive webview overlay — rendered on top via fixed inset-0 z-50.
          The records page below remains fully mounted and state is preserved. */}
      {driveWebview.open && (
        <DriveWebview
          files={driveWebview.files}
          onBack={() => setDriveWebview({open: false, files: []})}
        />
      )}

      <TabSectionCard
        title='Clinical Records'
        buttonText={hasData ? getText : undefined}
        onClick={handleDownloadAll}
        buttonLoading={sectionDownloading}
        extraActionButtons={extraActionButtons}
      >
        <Spin indicator={<Spinner loading />} spinning={loading}>
          {!loading && caseRecordsToDisplay.length === 0 ? (
            <EmptyState title='No Clinical Records' subTitle='No records have been uploaded yet.' />
          ) : (
            <div className='flex flex-col gap-4'>
              {caseRecordsToDisplay.map((record, index) => {
                const intraoral = record.intraoral_photo_files ?? []
                const extraoral = record.extraoral_photo_files ?? []
                const intraoralScanFiles = record.intraoral_scan_files ?? []
                const desiredOcclusionStoneCast = record.stone_cast_files ?? []
                const dicomFiles = record.dicom_files ?? []
                const xrays = record.radio_grap_files ?? []
                const externalLinks = (record.external_links ?? [])
                  .map((link) => String(link || '').trim())
                  .filter(Boolean)

                return (
                  <CollapseCardWithBorder
                    title={
                      <div className='flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-4 min-w-0 w-full'>
                        <div className='flex gap-3 md:gap-4 items-center min-w-0'>
                          <div className='flex justify-center rounded-2xl items-center w-12 h-12 md:w-16 md:h-16 shrink-0 bg-primaryColor'>
                            <FilesIcon color='#fff' size={20} />
                          </div>
                          <div className='min-w-0'>
                            <div className='text-sm md:text-base font-semibold truncate'>
                              Record {record.recordNumber}
                            </div>
                            <div className='text-xs md:text-sm font-medium text-slate-400 tracking-[0.14em]'>
                              {record?.created_at
                                ? dayjs(record.created_at).format('MMM DD, YYYY • hh:mm A')
                                : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                    position='end'
                    defaultOpen={index === 0}
                    key={record.id ?? index}
                  >
                    <div className='w-full min-w-0 flex flex-col gap-6 mt-4 overflow-hidden'>
                      <FileSections
                        title='Extra Oral'
                        icon={getIcon('Photographs')}
                        files={extraoral as any}
                        mode='thumbnail'
                        userId={userId}
                        dispatchAction={dispatchAction}
                        onOpenDriveWebview={(files) => setDriveWebview({open: true, files})}
                      />
                      <FileSections
                        title='Intraoral'
                        icon={getIcon('Photographs')}
                        files={intraoral as any}
                        mode='thumbnail'
                        userId={userId}
                        dispatchAction={dispatchAction}
                        onOpenDriveWebview={(files) => setDriveWebview({open: true, files})}
                      />
                      <FileSections
                        title='Intraoral Scan Files'
                        icon={getIcon('Scan files')}
                        files={intraoralScanFiles as any}
                        mode='list'
                        userId={userId}
                        dispatchAction={dispatchAction}
                        onOpenDriveWebview={(files) => setDriveWebview({open: true, files})}
                      />
                      <FileSections
                        title='Desired Occlusion on Stone Cast'
                        icon={getIcon('Scan files')}
                        files={desiredOcclusionStoneCast as any}
                        mode='list'
                        userId={userId}
                        dispatchAction={dispatchAction}
                        onOpenDriveWebview={(files) => setDriveWebview({open: true, files})}
                      />
                      <FileSections
                        title='DICOM Files'
                        icon={getIcon('Radiographs')}
                        files={dicomFiles as any}
                        mode='thumbnail'
                        useDocumentThumbnailIcon={serviceConfig?.VSP_PLANNING}
                        userId={userId}
                        dispatchAction={dispatchAction}
                        onOpenDriveWebview={(files) => setDriveWebview({open: true, files})}
                      />
                      <FileSections
                        title='Radiographs'
                        icon={getIcon('Radiographs')}
                        files={xrays as any}
                        mode='thumbnail'
                        useDocumentThumbnailIcon={serviceConfig?.VSP_PLANNING}
                        userId={userId}
                        dispatchAction={dispatchAction}
                        onOpenDriveWebview={(files) => setDriveWebview({open: true, files})}
                      />
                      {externalLinks.length > 0 ? (
                        <div className='flex flex-col gap-1 min-w-0'>
                          <div className='section-title'>External Links</div>
                          <div className='flex flex-col gap-2'>
                            {externalLinks.map((link, linkIndex) => (
                              <a
                                key={`${record.id}-external-link-${linkIndex}`}
                                href={link}
                                target='_blank'
                                rel='noreferrer'
                                className='flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-primaryColor hover:bg-slate-100 min-w-0'
                              >
                                <Link2 size={16} className='mt-0.5 shrink-0' />
                                <span className='min-w-0 break-all'>{link}</span>
                                <ExternalLink size={14} className='mt-0.5 shrink-0' />
                              </a>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </CollapseCardWithBorder>
                )
              })}
            </div>
          )}
        </Spin>
      </TabSectionCard>

      <Modal
        title={
          <div className='flex items-center gap-2'>
            <FileText size={20} className='text-primaryColor' />
            <span>Add Case Record</span>
          </div>
        }
        open={isCaseRecordFormOpen}
        onCancel={() => {
          if (isSubmittingForm) return
          setIsCaseRecordFormOpen(false)
          setShowExternalLinksConfirmModal(false)
          setExternalLinksInForm([])
        }}
        onOk={async () => {
          if (externalLinksInForm.length > 0) {
            setShowExternalLinksConfirmModal(true)
            return
          }
          await handleCaseRecordSubmit()
        }}
        okText={isUploading ? 'Uploading files...' : 'Submit'}
        okButtonProps={{
          disabled: isSubmitting,
          style: {
            background: '#4A62E8',
            borderColor: '#4462EA',
            color: 'white',
            opacity: isSubmitting ? 0.65 : 1,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
          },
        }}
        confirmLoading={isSubmitting}
        width={800}
        destroyOnClose
        styles={{body: {maxHeight: '70vh', overflowY: 'auto'}}}
      >
        <div className='p-2'>
          <VspCaseRecordsForm
            orderId={orderId || undefined}
            hideSectionHeader={true}
            onSubmitRef={(submitFn) => {
              submitRef.current = submitFn as any
            }}
            onCreateSuccess={() => {
              setIsSubmittingForm(false)
              setIsCaseRecordFormOpen(false)
              setShowExternalLinksConfirmModal(false)
              setExternalLinksInForm([])
              fetchCaseRecords()
            }}
            patientId={safeParseInt(patientId)}
            onExternalLinksChange={setExternalLinksInForm}
            onUploadingChange={setHasUploaderInProgress}
          />
        </div>
      </Modal>

      <Modal
        title='Confirm send'
        open={showExternalLinksConfirmModal}
        onCancel={() => !isSubmittingForm && setShowExternalLinksConfirmModal(false)}
        footer={[
          <button
            key='cancel'
            type='button'
            onClick={() => setShowExternalLinksConfirmModal(false)}
            disabled={isSubmittingForm}
            className='mr-3 rounded-xl border border-[#D0D5DD] px-4 py-2 text-sm font-semibold text-[#344054] transition-all hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-50'
          >
            Cancel
          </button>,
          <button
            key='confirm'
            type='button'
            onClick={async () => {
              setShowExternalLinksConfirmModal(false)
              await handleCaseRecordSubmit()
            }}
            disabled={isSubmittingForm}
            className='rounded-xl border border-[#4462EA] bg-[#4A62E8] px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-[#3E57DD] disabled:cursor-not-allowed disabled:opacity-50'
          >
            Confirm
          </button>,
        ]}
        destroyOnClose
      >
        <p className='text-sm leading-relaxed text-gray-500'>
          Before sending the files via Google Drive, please confirm that access has been provided to{' '}
          <a
            href={`mailto:${process.env.REACT_APP_VSP_CASES_EMAIL}`}
            className='font-semibold text-[#4A62E8] underline'
          >
            {process.env.REACT_APP_VSP_CASES_EMAIL}
          </a>{' '}
          for smooth processing &amp; next steps.
        </p>
      </Modal>
    </>
  )
}

export default VspRecords